import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import SuccessModal from '../../components/SuccessModal';
import SearchableSelect from '../../components/SearchableSelect';
import { useAuth } from '../../contexts/AuthContext';
import ProfilePhotoPicker from '../../components/ProfilePhotoPicker';

const CreateInterviewer = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [photoFile, setPhotoFile] = useState(null);
    const [supervisors, setSupervisors] = useState([]);
    const [loadingSupervisors, setLoadingSupervisors] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        password: '',
        zone: '',
        supervisor_id: '',
        ville: [],
        zone_locales: [],
        notes: []
    });

    const [currentVille, setCurrentVille] = useState('');
    const [currentZoneLocale, setCurrentZoneLocale] = useState('');
    const [currentNote, setCurrentNote] = useState('');
    const [cities, setCities] = useState([]);
    const [regions, setRegions] = useState([]);
    const [selectedRegion, setSelectedRegion] = useState('');
    const [citySearch, setCitySearch] = useState('');
    const [showCityDropdown, setShowCityDropdown] = useState(false);
    const [cityDropdownRef, setCityDropdownRef] = useState(null);

    const isSupervisor = user?.role === 'superviseur';

    // Fetch cities, regions, and supervisors when component mounts
    useEffect(() => {
        const fetchData = async () => {
            try {
                const promises = [
                    api.get('/cities'),
                    api.get('/cities/regions')
                ];

                const responses = await Promise.all(promises);

                if (responses[0].data.success) {
                    setCities(responses[0].data.data);
                }

                if (responses[1].data.success) {
                    setRegions(responses[1].data.data);
                }

                // Fetch supervisors separately if the user is not a supervisor
                if (!isSupervisor) {
                    setLoadingSupervisors(true);
                    try {
                        const supResponse = await api.get('/supervisors?exclude_suspended=1');
                        const supervisorData = supResponse.data.data || supResponse.data;
                        setSupervisors(Array.isArray(supervisorData) ? supervisorData : []);
                    } catch (err) {
                        console.error('Error fetching supervisors:', err);
                    } finally {
                        setLoadingSupervisors(false);
                    }
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        fetchData();
    }, [isSupervisor]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleArrayAdd = (field, value, setter) => {
        if (value.trim()) {
            setFormData(prev => ({
                ...prev,
                [field]: [...prev[field], value.trim()]
            }));
            setter('');
        }
    };

    const handleArrayRemove = (field, index) => {
        setFormData(prev => ({
            ...prev,
            [field]: prev[field].filter((_, i) => i !== index)
        }));
    };

    // City selection handlers
    const handleCitySelect = (city) => {
        if (!formData.ville.includes(city.name)) {
            setFormData(prev => ({
                ...prev,
                ville: [...prev.ville, city.name]
            }));
        }
        setCitySearch('');
        setShowCityDropdown(false);
    };

    const handleRegionChange = (region) => {
        setSelectedRegion(region);
        setCitySearch('');
        setShowCityDropdown(false);
    };

    const getFilteredCities = () => {
        console.log('getFilteredCities called with:', { cities: cities.length, selectedRegion, citySearch });
        let filtered = cities;
        
        if (selectedRegion) {
            filtered = filtered.filter(city => city.region === selectedRegion);
            console.log('After region filter:', filtered.length);
        }
        
        if (citySearch) {
            filtered = filtered.filter(city => 
                city.name.toLowerCase().includes(citySearch.toLowerCase())
            );
            console.log('After search filter:', filtered.length);
        }
        
        const result = filtered.slice(0, 10); // Limit to 10 results
        console.log('Final result:', result.length);
        return result;
    };

    // Click outside handler for city dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (cityDropdownRef && !cityDropdownRef.contains(event.target)) {
                setShowCityDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [cityDropdownRef]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors({});

        // Phone is mandatory for supervisors: 05/06/07 followed by 8 digits.
        const phone = formData.phone.trim();
        if (isSupervisor && !phone) {
            setErrors({ phone: ['Le numéro de téléphone est obligatoire.'] });
            setLoading(false);
            return;
        }
        if (phone && !/^0[567][0-9]{8}$/.test(phone)) {
            setErrors({ phone: ['Le numéro de téléphone doit commencer par 05, 06 ou 07 et contenir 10 chiffres.'] });
            setLoading(false);
            return;
        }

        const submitData = { ...formData, phone };

        try {
            const response = await api.post('/interviewers', submitData);

            if (response.status === 201) {
                // The photo can only be sent once the enquêteur exists (it is keyed by id).
                // A failure here must not lose the created record — it is reported, not thrown.
                const newId = response.data?.data?.id;
                let photoWarning = '';
                if (photoFile && newId) {
                    try {
                        const form = new FormData();
                        form.append('photo', photoFile, 'photo.jpg');
                        await api.post(`/interviewers/${newId}/photo`, form, {
                            headers: { 'Content-Type': 'multipart/form-data' },
                        });
                    } catch (photoError) {
                        console.error('Photo upload failed:', photoError);
                        photoWarning = " Attention : la photo n'a pas pu être enregistrée, vous pouvez l'ajouter depuis sa fiche.";
                    }
                }
                setSuccessMessage('Enquêteur créé avec succès !' + photoWarning);
                setShowSuccessModal(true);
                // Navigate after modal is shown
                setTimeout(() => {
                    navigate('/interviewers');
                }, 1500);
            }
        } catch (error) {
            if (error.response?.status === 422) {
                setErrors(error.response.data.errors);
            } else {
                setSuccessMessage('Erreur lors de la création de l\'enquêteur');
                setShowSuccessModal(true);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 relative overflow-hidden py-8">
            {/* Dotted Background */}
            <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                    backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
                    backgroundSize: '20px 20px'
                }}></div>
            </div>
            <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="bg-white shadow-xl rounded-lg">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h1 className="text-2xl font-bold text-gray-900">Créer un Enquêteur</h1>
                        <p className="mt-1 text-sm text-gray-600">
                            Remplissez les informations pour créer un nouvel enquêteur
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="p-6 space-y-6">
                        {/* Photo — uploaded right after creation, once the id exists */}
                        <ProfilePhotoPicker onSelect={setPhotoFile} />

                        {/* Basic Information */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm !font-bold text-gray-700 mb-2">
                                    Nom complet *
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                        errors.name ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    placeholder="Nom complet de l'enquêteur"
                                />
                                {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name[0]}</p>}
                            </div>

                            <div>
                                <label className="block text-sm !font-bold text-gray-700 mb-2">
                                    Email *
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                        errors.email ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    placeholder="email@exemple.com"
                                />
                                {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email[0]}</p>}
                            </div>

                            <div>
                                <label className="block text-sm !font-bold text-gray-700 mb-2">
                                    Téléphone{isSupervisor ? ' *' : ''}
                                </label>
                                <input
                                    type="tel"
                                    name="phone"
                                    inputMode="numeric"
                                    maxLength={10}
                                    value={formData.phone}
                                    onChange={(e) => handleInputChange({ target: { name: 'phone', value: e.target.value.replace(/\D/g, '') } })}
                                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                        errors.phone ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    placeholder="06XXXXXXXX"
                                />
                                {errors.phone
                                    ? <p className="mt-1 text-sm text-red-600">{errors.phone[0]}</p>
                                    : <p className="mt-1 text-xs text-gray-500">10 chiffres, commence par 05, 06 ou 07</p>}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm !font-bold text-gray-700 mb-2">
                                    Mot de passe *
                                </label>
                                <div className="relative">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        name="password"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        className={`w-full px-3 py-2 pr-10 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                            errors.password ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                        placeholder="Mot de passe (min. 8 caractères)"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword((v) => !v)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                                    >
                                        <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                    </button>
                                </div>
                                {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password[0]}</p>}
                            </div>

                            <div>
                                <label className="block text-sm !font-bold text-gray-700 mb-2">
                                    Zone
                                </label>
                                <select
                                    name="zone"
                                    value={formData.zone}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Sélectionner une zone</option>
                                    <option value="rural">Rural</option>
                                    <option value="urbain">Urbain</option>
                                </select>
                            </div>
                        </div>

                        {/* Supervisor Selection - only for non-supervisor users */}
                        {!isSupervisor && (
                            <div>
                                <label className="block text-sm !font-bold text-gray-700 mb-2">
                                    Prestataire *
                                </label>
                                <SearchableSelect
                                    options={supervisors}
                                    selectedValue={formData.supervisor_id || null}
                                    onChange={(value) => {
                                        setFormData(prev => ({ ...prev, supervisor_id: value || '' }));
                                        if (errors.supervisor_id) {
                                            setErrors(prev => ({ ...prev, supervisor_id: '' }));
                                        }
                                    }}
                                    placeholder="Sélectionner un prestataire"
                                    searchPlaceholder="Rechercher un prestataire..."
                                    getOptionLabel={(sup) => sup.name}
                                    getOptionMeta={(sup) => [sup.ref, sup.region].filter(Boolean).join(' - ')}
                                    loading={loadingSupervisors}
                                />
                                {errors.supervisor_id && <p className="mt-1 text-sm text-red-600">{errors.supervisor_id[0]}</p>}
                            </div>
                        )}

                        {/* Villes */}
                        <div>
                            <label className="block text-sm !font-bold text-gray-700 mb-2">
                                Villes
                            </label>
                            
                            {/* Region Filter */}
                            <div className="mb-3">
                                <select
                                    value={selectedRegion}
                                    onChange={(e) => handleRegionChange(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Toutes les régions</option>
                                    {regions.map((region, index) => (
                                        <option key={index} value={region}>
                                            {region}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* City Search */}
                            <div className="relative mb-3" ref={setCityDropdownRef}>
                                <input
                                    type="text"
                                    value={citySearch}
                                    onChange={(e) => {
                                        setCitySearch(e.target.value);
                                        setShowCityDropdown(true);
                                    }}
                                    onFocus={() => setShowCityDropdown(true)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Rechercher une ville..."
                                />
                                
                                {/* City Dropdown */}
                                {showCityDropdown && (
                                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                        {getFilteredCities().length > 0 ? (
                                            getFilteredCities().map((city, index) => (
                                                <div
                                                    key={index}
                                                    className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                                                    onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        handleCitySelect(city);
                                                    }}
                                                >
                                                    <div className="font-medium">{city.name}</div>
                                                    <div className="text-sm text-gray-500">{city.region}</div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="px-3 py-2 text-gray-500">
                                                Aucune ville trouvée
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Selected Cities */}
                            {formData.ville.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                    {formData.ville.map((ville, index) => (
                                        <span
                                            key={index}
                                            className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-red-100 text-red-800"
                                        >
                                            {ville}
                                            <button
                                                type="button"
                                                onClick={() => handleArrayRemove('ville', index)}
                                                className="ml-2 text-red-600 hover:text-red-800"
                                            >
                                                ×
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Zones Locales */}
                        <div>
                            <label className="block text-sm !font-bold text-gray-700 mb-2">
                                Zones Locales
                            </label>
                            <div className="flex flex-col sm:flex-row gap-2 mb-2">
                                <input
                                    type="text"
                                    value={currentZoneLocale}
                                    onChange={(e) => setCurrentZoneLocale(e.target.value)}
                                    className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Ajouter une zone locale"
                                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleArrayAdd('zone_locales', currentZoneLocale, setCurrentZoneLocale))}
                                />
                                <button
                                    type="button"
                                    onClick={() => handleArrayAdd('zone_locales', currentZoneLocale, setCurrentZoneLocale)}
                                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 flex-shrink-0"
                                >
                                    Ajouter
                                </button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {formData.zone_locales.map((zone, index) => (
                                    <span
                                        key={index}
                                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800"
                                    >
                                        {zone}
                                        <button
                                            type="button"
                                            onClick={() => handleArrayRemove('zone_locales', index)}
                                            className="ml-2 text-green-600 hover:text-green-800"
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>



                        {/* Notes */}
                        <div>
                            <label className="block text-sm !font-bold text-gray-700 mb-2">
                                Notes
                            </label>
                            <div className="flex flex-col sm:flex-row gap-2 mb-2">
                                <input
                                    type="text"
                                    value={currentNote}
                                    onChange={(e) => setCurrentNote(e.target.value)}
                                    className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Ajouter une note"
                                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleArrayAdd('notes', currentNote, setCurrentNote))}
                                />
                                <button
                                    type="button"
                                    onClick={() => handleArrayAdd('notes', currentNote, setCurrentNote)}
                                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 flex-shrink-0"
                                >
                                    Ajouter
                                </button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {formData.notes.map((note, index) => (
                                    <span
                                        key={index}
                                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-yellow-100 text-yellow-800"
                                    >
                                        {note}
                                        <button
                                            type="button"
                                            onClick={() => handleArrayRemove('notes', index)}
                                            className="ml-2 text-yellow-600 hover:text-yellow-800"
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>

                        {/* Submit Buttons */}
                        <div className="flex flex-col sm:flex-row justify-end gap-3 sm:space-x-4 pt-6 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={() => navigate('/interviewers')}
                                className="w-full sm:w-auto px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                            >
                                Annuler
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full sm:w-auto px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? 'Création...' : 'Créer l\'Enquêteur'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Success Modal */}
            <SuccessModal
                isOpen={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                message={successMessage}
                duration={3000}
            />
        </div>
    );
};

export default CreateInterviewer;
